import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Refresh, FileDownload, ArrowBack } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { exportStyledExcel } from '../../../utils/excelExport';

function VendorandItemWisePurchaseReport() {
    const [dataItem, setDataItem] = useState([]);
    const history = useHistory();
    const [searchQuery, setSearchQuery] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [PartyandItemWise, setPartyandItemWise] = useState('1');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [error, setError] = useState('');
    const { isAuthenticated, companyId, branchId, token, logout, username, companyName } = useAuth();
    const [selectedCustomers, setCustomerType] = useState('0');
    const [selectedItem, setSelectedItem] = useState('0');

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        if (fromDate && toDate && companyId && branchId && token) {
            if (new Date(fromDate) > new Date(toDate)) {
                setDataItem([]);
                setError('From Date cannot be greater than To Date');
                return;
            }
            fetchTableData();
        }
    }, [fromDate, toDate, companyId, branchId, token, PartyandItemWise, selectedCustomers, selectedItem]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}-01`;
    };

    const fetchTableData = async () => {
        if (new Date(fromDate) > new Date(toDate)) {
            setError('From Date cannot be greater than To Date');
            return;
        }
        setDataItem([]);

        // Delay showing loading overlay to prevent flash on quick loads
        const loadingTimeout = setTimeout(() => {
            setLoading(true);
            setLoadingMessage('Loading purchase report...');
        }, 300);

        try {
            if (PartyandItemWise === '1') {
                const response = await fetchData('ReportApi', `/VendorPurchaseWise/${companyId}/${selectedCustomers}/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } else if (PartyandItemWise === '2') {
                const response = await fetchData('ReportApi', `/ItemWisePurchase/${companyId}/${selectedItem}/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            }
        } catch (error) {
            console.error('Error fetching Purchase Report:', error);
        } finally {
            clearTimeout(loadingTimeout);
            setLoading(false);
        }
    };

    const handleFromDateChange = (e) => {
        setFromDate(e.target.value);
    };

    const handleToDateChange = (e) => {
        setToDate(e.target.value);
    };

    const handlesetPartyandItemWiseChange = (event) => {
        setPartyandItemWise(event.target.value);
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportCustomerWiseExcel = async () => {
        if (!filteredDataItem.length) return;
        // Columns match Party Wise display: Bill Date | Bill Number | Item Name | Quantity | Rate | Taxable Amount | GST Rate | CGST Amount | SGST Amount | IGST Amount | Total Amount
        const colHeaders = ['Bill Date', 'Bill Number', 'Item Name', 'Quantity', 'Rate', 'Taxable Amount', 'GST Rate', 'CGST Amount', 'SGST Amount', 'IGST Amount', 'Total Amount'];
        const rows = [];

        // Group by vendor (same as display)
        const vendorGrouped = filteredDataItem.reduce((acc, r) => {
            if (!acc[r.MasterName]) acc[r.MasterName] = [];
            acc[r.MasterName].push(r);
            return acc;
        }, {});

        Object.entries(vendorGrouped).forEach(([vendor, items]) => {
            // Vendor header row — merged across all columns
            rows.push([`__MERGE__Vendor: ${vendor}`, '', '', '', '', '', '', '', '', '', '']);

            // Group by bill number within vendor (same as display)
            const billMap = {};
            const billOrder = [];
            items.forEach(item => {
                if (!billMap[item.BillNumber]) { billMap[item.BillNumber] = []; billOrder.push(item.BillNumber); }
                billMap[item.BillNumber].push(item);
            });

            billOrder.forEach(billNo => {
                const billItems = billMap[billNo];
                const billDate = formatDate(billItems[0].BillDate);
                const billTotal = billItems.reduce((s, r) => s + Number(r.TotalAmount || 0), 0);

                // Bill sub-header row: Bill Date | Bill No | blank cols | Bill Total as number in last col
                rows.push([billDate, billNo, `Bill Total`, '', '', '', '', '', '', '', billTotal]);

                // Item rows (blank Bill Date & Bill No cols, then item data)
                billItems.forEach(r => {
                    rows.push([
                        '',
                        '',
                        r.ItemName,
                        Number(r.Quantity) || 0,
                        Number(r.Rate) || 0,
                        Number(r.TaxableAmount) || 0,
                        `${r.GSTRate}%`,
                        Number(r.CGSTAmount) || 0,
                        Number(r.SGSTAmount) || 0,
                        Number(r.IGSTAmount) || 0,
                        Number(r.TotalAmount) || 0,
                    ]);
                });
            });

            // No subtotal per vendor — matches display
        });

        const gQty = filteredDataItem.reduce((s, r) => s + Number(r.Quantity || 0), 0);
        const gTax = filteredDataItem.reduce((s, r) => s + Number(r.TaxableAmount || 0), 0);
        const gCGST = filteredDataItem.reduce((s, r) => s + Number(r.CGSTAmount || 0), 0);
        const gSGST = filteredDataItem.reduce((s, r) => s + Number(r.SGSTAmount || 0), 0);
        const gIGST = filteredDataItem.reduce((s, r) => s + Number(r.IGSTAmount || 0), 0);
        const gTotal = filteredDataItem.reduce((s, r) => s + Number(r.TotalAmount || 0), 0);
        const totalsRow = ['', 'Grand Total', '', gQty, '', gTax, '', gCGST, gSGST, gIGST, gTotal];

        await exportStyledExcel({
            reportTitle: 'PURCHASE VENDOR WISE REPORT',
            sheetName: 'Vendor Wise',
            fileName: `Purchase_VendorWise_${fromDate}_${toDate}.xlsx`,
            companyName, username, fromDate, toDate,
            headers: colHeaders,
            rows,
            totalsRow,
            columnWidths: [13, 14, 28, 9, 10, 12, 8, 11, 11, 11, 13],
        });
    };

    const handleExportItemWiseExcel = async () => {
        if (!filteredDataItem.length) return;
        // Columns match Item Wise display: Item Name | Customer | Bill Date | Bill Number | Quantity | Rate | Taxable Amount | GST Rate | CGST Amount | SGST Amount | IGST Amount | Total Amount
        const colHeaders = ['Item Name', 'Vendor', 'Bill Date', 'Bill Number', 'Quantity', 'Rate', 'Taxable Amount', 'GST Rate', 'CGST Amount', 'SGST Amount', 'IGST Amount', 'Total Amount'];
        const rows = [];

        // Group by item (same as display)
        const itemGrouped = filteredDataItem.reduce((acc, r) => {
            if (!acc[r.ItemName]) acc[r.ItemName] = [];
            acc[r.ItemName].push(r);
            return acc;
        }, {});

        Object.entries(itemGrouped).forEach(([itemName, items]) => {
            // Item header row — merged across all columns
            rows.push([`__MERGE__Item: ${itemName}`, '', '', '', '', '', '', '', '', '', '', '']);

            // Data rows — same columns as display
            items.forEach(r => {
                rows.push([
                    r.ItemName,
                    r.MasterName,
                    formatDate(r.BillDate),
                    r.BillNumber,
                    Number(r.Quantity) || 0,
                    Number(r.Rate) || 0,
                    Number(r.TaxableAmount) || 0,
                    `${r.GSTRate}%`,
                    Number(r.CGSTAmount) || 0,
                    Number(r.SGSTAmount) || 0,
                    Number(r.IGSTAmount) || 0,
                    Number(r.TotalAmount) || 0,
                ]);
            });

            // No subtotal per item — matches display
        });

        const gQty = filteredDataItem.reduce((s, r) => s + Number(r.Quantity || 0), 0);
        const gTax = filteredDataItem.reduce((s, r) => s + Number(r.TaxableAmount || 0), 0);
        const gCGST = filteredDataItem.reduce((s, r) => s + Number(r.CGSTAmount || 0), 0);
        const gSGST = filteredDataItem.reduce((s, r) => s + Number(r.SGSTAmount || 0), 0);
        const gIGST = filteredDataItem.reduce((s, r) => s + Number(r.IGSTAmount || 0), 0);
        const gTotal = filteredDataItem.reduce((s, r) => s + Number(r.TotalAmount || 0), 0);
        const totalsRow = ['Grand Total', '', '', '', gQty, '', gTax, '', gCGST, gSGST, gIGST, gTotal];

        await exportStyledExcel({
            reportTitle: 'PURCHASE ITEM WISE REPORT',
            sheetName: 'Item Wise',
            fileName: `Purchase_ItemWise_${fromDate}_${toDate}.xlsx`,
            companyName, username, fromDate, toDate,
            headers: colHeaders,
            rows,
            totalsRow,
            columnWidths: [28, 22, 13, 14, 9, 10, 12, 8, 11, 11, 11, 13],
        });
    };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>
            {loading && (
                <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.9)', zIndex: 9999, backdropFilter: 'blur(4px)' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size={60} thickness={4} sx={{ color: '#bf360c', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#bf360c', fontWeight: 600, fontSize: '1rem' }}>{loadingMessage}</Typography>
                    </Box>
                </Box>
            )}
            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: 'linear-gradient(135deg, #d4500a 0%, #e8734a 100%)', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Vendor &amp; Item Wise Purchase Report</span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input type="date" value={fromDate} onChange={handleFromDateChange} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={handleToDateChange} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <FormControl component="fieldset">
                        <RadioGroup row value={PartyandItemWise} onChange={handlesetPartyandItemWiseChange} style={{ gap: '8px', marginLeft: '4px' }}>
                            <FormControlLabel value="1" control={<Radio size="small" sx={{ marginleft: '5px', padding: '2px', '& .MuiSvgIcon-root': { fontSize: 15 }, color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: '#fff' } }} />} label={<span style={{ fontSize: '11px', color: '#fff' }}>Party Wise</span>} />
                            <FormControlLabel value="2" control={<Radio size="small" sx={{ padding: '2px', '& .MuiSvgIcon-root': { fontSize: 15 }, color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: '#fff' } }} />} label={<span style={{ fontSize: '11px', color: '#fff' }}>Item Wise</span>} />
                        </RadioGroup>
                    </FormControl>
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchTableData} disabled={loading} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={PartyandItemWise === '1' ? handleExportCustomerWiseExcel : handleExportItemWiseExcel} disabled={!filteredDataItem.length} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
                            <FileDownload sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Scrollable table area */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>

                    {/* Table Section - Party Wise */}
                    {PartyandItemWise === '1' && (
                        <div style={{ marginBottom: isMobile ? '80px' : '8px' }}>
                            <TableContainer component={Paper} style={{
                                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                                borderRadius: '6px',
                                overflow: 'auto',
                                maxHeight: 'calc(100vh - 120px)'
                            }}>
                                <Table stickyHeader sx={{ minWidth: isMobile ? 900 : 650 }} size="small">
                                    <TableHead>
                                        <TableRow style={{
                                            background: '#e8734a',
                                            height: '24px'
                                        }}>
                                            {['Bill Date', 'Bill Number', 'Item Name', 'Quantity', 'Rate', 'Taxable Amount', 'GST Rate', 'CGST Amount', 'SGST Amount', 'IGST Amount', 'Total Amount'].map((col) => (
                                                <TableCell
                                                    key={col}
                                                    style={{
                                                        color: 'white',
                                                        fontWeight: '600',
                                                        fontSize: isMobile ? '8px' : '9px',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.2px',
                                                        padding: isMobile ? '2px 4px' : '2px 6px',
                                                        textAlign: ['Bill Date', 'Bill Number', 'Item Name'].includes(col) ? 'left' : 'right',
                                                        background: '#e8734a',
                                                        height: '24px',
                                                        fontSize: '11px'
                                                    }}
                                                >
                                                    {col}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {Object.entries(
                                            filteredDataItem.reduce((acc, item) => {
                                                if (!acc[item.MasterName]) acc[item.MasterName] = [];
                                                acc[item.MasterName].push(item);
                                                return acc;
                                            }, {})
                                        ).map(([customerName, items], customerIndex) => {
                                            return (
                                                <React.Fragment key={customerIndex}>
                                                    <TableRow sx={{ backgroundColor: '#fbe9e7' }}>
                                                        <TableCell
                                                            colSpan={11}
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                fontSize: isMobile ? '10px' : '12px',
                                                                color: '#004d40',
                                                                padding: isMobile ? '4px' : '6px 8px'
                                                            }}
                                                        >
                                                            {customerName}
                                                        </TableCell>
                                                    </TableRow>

                                                    {(() => {
                                                        // Group items by BillNumber, preserving order
                                                        const billMap = {};
                                                        const billOrder = [];
                                                        items.forEach((item) => {
                                                            if (!billMap[item.BillNumber]) {
                                                                billMap[item.BillNumber] = [];
                                                                billOrder.push(item.BillNumber);
                                                            }
                                                            billMap[item.BillNumber].push(item);
                                                        });

                                                        return billOrder.map((billNo) => {
                                                            const billItems = billMap[billNo];
                                                            const billDate = billItems[0].BillDate;
                                                            const billTotal = billItems.reduce((s, r) => s + Number(r.TotalAmount || 0), 0);

                                                            return (
                                                                <React.Fragment key={billNo}>
                                                                    {/* Bill sub-header */}
                                                                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                                                        <TableCell sx={{ py: 0.4, px: isMobile ? 0.5 : 0.75, borderBottom: '1px solid #90caf9' }}>
                                                                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#1565c0' }}>
                                                                                {formatDate(billDate)}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell sx={{ py: 0.4, px: isMobile ? 0.5 : 0.75, borderBottom: '1px solid #90caf9' }}>
                                                                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#1565c0' }}>
                                                                                {billNo}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell colSpan={9} sx={{ py: 0.4, px: isMobile ? 0.5 : 0.75, textAlign: 'right', borderBottom: '1px solid #90caf9' }}>
                                                                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#1565c0' }}>
                                                                                Bill Total: {billTotal.toFixed(2)}
                                                                            </Typography>
                                                                        </TableCell>
                                                                    </TableRow>

                                                                    {/* Bill item rows */}
                                                                    {billItems.map((data, idx) => (
                                                                        <TableRow
                                                                            key={idx}
                                                                            sx={{
                                                                                '&:nth-of-type(even)': { backgroundColor: '#fff8f7' },
                                                                                '&:hover': { backgroundColor: '#fce4e1', transition: 'all 0.15s ease-in-out' },
                                                                                borderBottom: '1px solid #fce4e1',
                                                                                height: '28px'
                                                                            }}
                                                                        >
                                                                            <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }} />
                                                                            <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }} />
                                                                            <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                                                    {data.ItemName}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                                    {data.Quantity}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                                    {data.Rate.toFixed(2)}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                                    {data.TaxableAmount.toFixed(2)}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                                    {data.GSTRate}%
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                                    {data.CGSTAmount.toFixed(2)}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                                    {data.SGSTAmount.toFixed(2)}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                                    {data.IGSTAmount.toFixed(2)}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            {/* <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }} /> */}
                                                                             <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                                    {data.TotalAmount.toFixed(2)}
                                                                                </Typography>
                                                                            </TableCell>
                                                                        
                                                                    </TableRow>
                                                            ))
                                                    }


                                                            </React.Fragment>
                                            );
                                        });
                                                })()}

                                    </React.Fragment>
                                    );
                                    })}
                                </TableBody>
                                {filteredDataItem.length > 0 && (() => {
                                    const gQty = filteredDataItem.reduce((s, r) => s + Number(r.Quantity || 0), 0);
                                    const gTax = filteredDataItem.reduce((s, r) => s + Number(r.TaxableAmount || 0), 0);
                                    const gCGST = filteredDataItem.reduce((s, r) => s + Number(r.CGSTAmount || 0), 0);
                                    const gSGST = filteredDataItem.reduce((s, r) => s + Number(r.SGSTAmount || 0), 0);
                                    const gIGST = filteredDataItem.reduce((s, r) => s + Number(r.IGSTAmount || 0), 0);
                                    const gTotal = filteredDataItem.reduce((s, r) => s + Number(r.TotalAmount || 0), 0);
                                    const ftd = { py: 0.6, px: isMobile ? 0.5 : 0.75, fontWeight: 700, fontSize: '0.72rem', color: '#fff', background: '#d4500a', textAlign: 'right' };
                                    return (
                                        <tfoot>
                                            <TableRow sx={{ background: '#d4500a', position: 'sticky', bottom: 0, zIndex: 1 }}>
                                                <TableCell colSpan={3} sx={{ ...ftd, textAlign: 'left' }}>Grand Total</TableCell>
                                                <TableCell sx={ftd}>{gQty.toFixed(2)}</TableCell>
                                                <TableCell sx={ftd}></TableCell>
                                                <TableCell sx={ftd}>{gTax.toFixed(2)}</TableCell>
                                                <TableCell sx={ftd}></TableCell>
                                                <TableCell sx={ftd}>{gCGST.toFixed(2)}</TableCell>
                                                <TableCell sx={ftd}>{gSGST.toFixed(2)}</TableCell>
                                                <TableCell sx={ftd}>{gIGST.toFixed(2)}</TableCell>
                                                <TableCell sx={ftd}>{gTotal.toFixed(2)}</TableCell>
                                            </TableRow>
                                        </tfoot>
                                    );
                                })()}
                            </Table>
                        </TableContainer>
                    </div>
                )}

                {/* Table Section - Item Wise */}
                {PartyandItemWise === '2' && (
                    <div style={{ marginBottom: isMobile ? '80px' : '8px' }}>
                        <TableContainer component={Paper} style={{
                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                            borderRadius: '6px',
                            overflow: 'auto',
                            maxHeight: 'calc(100vh - 120px)'
                        }}>
                            <Table stickyHeader sx={{ minWidth: isMobile ? 900 : 650 }} size="small">
                                <TableHead>
                                    <TableRow style={{
                                        background: '#e8734a',
                                        height: '24px'
                                    }}>
                                        {['Item Name', 'Customer', 'Bill Date', 'Bill Number', 'Quantity', 'Rate', 'Taxable Amount', 'GST Rate', 'CGST Amount', 'SGST Amount', 'IGST Amount', 'Total Amount'].map((col) => (
                                            <TableCell
                                                key={col}
                                                style={{
                                                    color: 'white',
                                                    fontWeight: '600',
                                                    fontSize: isMobile ? '8px' : '9px',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.2px',
                                                    padding: isMobile ? '2px 4px' : '2px 6px',
                                                    textAlign: ['Item Name', 'Customer', 'Bill Date', 'Bill Number'].includes(col) ? 'left' : 'right',
                                                    height: '24px',
                                                    fontSize: '11px',
                                                    background: '#e8734a',
                                                }}
                                            >
                                                {col}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {Object.entries(
                                        filteredDataItem.reduce((acc, item) => {
                                            if (!acc[item.ItemName]) acc[item.ItemName] = [];
                                            acc[item.ItemName].push(item);
                                            return acc;
                                        }, {})
                                    ).map(([itemName, customers], itemIndex) => {

                                        return (
                                            <React.Fragment key={itemIndex}>
                                                <TableRow sx={{ backgroundColor: '#fbe9e7' }}>
                                                    <TableCell
                                                        colSpan={12}
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            fontSize: isMobile ? '10px' : '12px',
                                                            color: '#004d40',
                                                            padding: isMobile ? '4px' : '6px 8px'
                                                        }}
                                                    >
                                                        {itemName}
                                                    </TableCell>
                                                </TableRow>

                                                {customers.map((data, index) => (
                                                    <TableRow
                                                        key={index}
                                                        sx={{
                                                            '&:nth-of-type(even)': { backgroundColor: '#fff8f7' },
                                                            '&:hover': {
                                                                backgroundColor: '#fce4e1',
                                                                transition: 'all 0.15s ease-in-out'
                                                            },
                                                            borderBottom: '1px solid #fce4e1',
                                                            height: '28px'
                                                        }}
                                                    >
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                                {data.ItemName}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                                {data.MasterName}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 500, color: '#374151' }}>
                                                                {formatDate(data.BillDate)}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                                {data.BillNumber}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                {data.Quantity}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                {data.Rate.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                {data.TaxableAmount.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                {data.GSTRate}%
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                {data.CGSTAmount.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                {data.SGSTAmount.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                {data.IGSTAmount.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 700, color: '#1e40af' }}>
                                                                {data.TotalAmount.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}


                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                                {filteredDataItem.length > 0 && (() => {
                                    const gQty = filteredDataItem.reduce((s, r) => s + Number(r.Quantity || 0), 0);
                                    const gTax = filteredDataItem.reduce((s, r) => s + Number(r.TaxableAmount || 0), 0);
                                    const gCGST = filteredDataItem.reduce((s, r) => s + Number(r.CGSTAmount || 0), 0);
                                    const gSGST = filteredDataItem.reduce((s, r) => s + Number(r.SGSTAmount || 0), 0);
                                    const gIGST = filteredDataItem.reduce((s, r) => s + Number(r.IGSTAmount || 0), 0);
                                    const gTotal = filteredDataItem.reduce((s, r) => s + Number(r.TotalAmount || 0), 0);
                                    const ftd = { py: 0.6, px: isMobile ? 0.5 : 0.75, fontWeight: 700, fontSize: '0.72rem', color: '#fff', background: '#d4500a', textAlign: 'right' };
                                    return (
                                        <tfoot>
                                            <TableRow sx={{ background: '#d4500a', position: 'sticky', bottom: 0, zIndex: 1 }}>
                                                <TableCell colSpan={4} sx={{ ...ftd, textAlign: 'left' }}>Grand Total</TableCell>
                                                <TableCell sx={ftd}>{gQty.toFixed(2)}</TableCell>
                                                <TableCell sx={ftd}></TableCell>
                                                <TableCell sx={ftd}>{gTax.toFixed(2)}</TableCell>
                                                <TableCell sx={ftd}></TableCell>
                                                <TableCell sx={ftd}>{gCGST.toFixed(2)}</TableCell>
                                                <TableCell sx={ftd}>{gSGST.toFixed(2)}</TableCell>
                                                <TableCell sx={ftd}>{gIGST.toFixed(2)}</TableCell>
                                                <TableCell sx={ftd}>{gTotal.toFixed(2)}</TableCell>
                                            </TableRow>
                                        </tfoot>
                                    );
                                })()}
                            </Table>
                        </TableContainer>
                    </div>
                )}
            </div>{/* end scrollable */}
        </div>
        </div >
    );
}

export default VendorandItemWisePurchaseReport;

